// src/pages/ITSCommissioning.tsx — SCATS/ITS commissioning checklists
import { useState } from 'react'
import { useAuthStore } from '../stores/auth.store'
import { MOCK_ITS_COMMISSIONING, ITS_RESULT_META, type ITSItem } from '../utils/mockDataTier5'
import { formatDate } from '../utils/formatters'
import { Radio, CheckCircle, AlertTriangle, X, ChevronDown, ChevronUp, Clock, Zap } from 'lucide-react'
import { MOCK_TASKS } from '../utils/mockDataDrawings'
import { MOCK_CONFIGURED_TASKS, MOCK_MOBILE_TASK_RECORDS, MOBILE_TASK_META } from '../utils/mockDataMobile'

type ITSRecord = typeof MOCK_ITS_COMMISSIONING[0]
type Phase     = ITSRecord['phases'][0]

// Record result modal
function RecordModal({ item, onClose, onRecord }: { item: ITSItem; onClose: () => void; onRecord: (r: string, notes: string) => void }) {
  const { user } = useAuthStore()
  const [result, setResult] = useState<string>('')
  const [notes, setNotes]   = useState('')
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="font-semibold text-gray-900 text-sm">Record inspection result</h2>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="bg-gray-50 rounded-lg p-3 text-sm">
            <p className="font-medium text-gray-900">{item.item}</p>
            {item.critical && <p className="text-xs text-red-600 mt-1 font-semibold">★ Critical item</p>}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">Result</label>
            <div className="grid grid-cols-3 gap-2">
              {['PASS','FAIL','NA'].map(r => {
                const m = ITS_RESULT_META[r]
                return (
                  <button key={r} type="button" onClick={() => setResult(r)}
                    className={`py-2.5 rounded-lg border-2 text-xs font-bold transition-colors ${result === r ? `border-current ${m.bg} ${m.text}` : 'border-gray-200 text-gray-400 hover:border-gray-300'}`}>
                    {m.label}
                  </button>
                )
              })}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Notes (optional)</label>
            <textarea className="input h-16 resize-none" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Observations, equipment readings, anomalies…" />
          </div>
          <div className="text-xs text-gray-400">Inspector: {user?.fullName} · {new Date().toLocaleDateString('en-AU')}</div>
        </div>
        <div className="px-6 py-4 border-t flex justify-end gap-3">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={() => { if (result) { onRecord(result, notes); onClose() } }} disabled={!result}
            className={`btn-primary ${result === 'FAIL' ? 'bg-red-600 hover:bg-red-700' : result === 'PASS' ? 'bg-green-600 hover:bg-green-700' : ''}`}>
            Record {result ? ITS_RESULT_META[result]?.label : '—'}
          </button>
        </div>
      </div>
    </div>
  )
}

// Phase accordion
function PhaseSection({ phase, phaseIndex, onRecord, canRecord }: {
  phase: Phase; phaseIndex: number
  onRecord: (phaseIndex: number, itemId: string, result: string, notes: string, inspector: string) => void
  canRecord: boolean
}) {
  const [open, setOpen] = useState(phaseIndex === 0)
  const [recording, setRecording] = useState<ITSItem | null>(null)
  const { user } = useAuthStore()

  const passCount = phase.items.filter(i => i.result === 'PASS' || i.result === 'NA').length
  const failCount = phase.items.filter(i => i.result === 'FAIL').length
  const doneCount = phase.items.filter(i => i.result !== null).length
  const pct       = Math.round(doneCount / phase.items.length * 100)
  const allPassed = doneCount === phase.items.length && failCount === 0

  const phaseColor =
    failCount > 0   ? 'bg-red-500' :
    allPassed        ? 'bg-green-500' :
    doneCount > 0    ? 'bg-blue-500' : 'bg-gray-200'

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 px-5 py-4 bg-gray-50 hover:bg-gray-100 transition-colors">
        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0 ${phaseColor}`}>
          {allPassed ? '✓' : phaseIndex + 1}
        </div>
        <div className="flex-1 text-left">
          <div className="font-semibold text-sm text-gray-900">{phase.label}</div>
          <div className="text-xs text-gray-500 mt-0.5">{doneCount} / {phase.items.length} items · {pct}% complete{failCount > 0 ? ` · ${failCount} FAIL` : ''}</div>
        </div>
        <div className="flex items-center gap-3">
          {/* Mini progress bar */}
          <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden hidden sm:block">
            <div className={`h-full rounded-full ${phaseColor}`} style={{ width: `${pct}%` }} />
          </div>
          {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </button>

      {open && (
        <div className="divide-y divide-gray-100">
          {phase.items.map(item => {
            const rm = item.result ? ITS_RESULT_META[item.result] : ITS_RESULT_META.NOT_CHECKED
            return (
              <div key={item.id} className={`flex items-start gap-3 px-5 py-3 ${item.result === 'FAIL' ? 'bg-red-50' : ''}`}>
                <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${item.result ? (item.result === 'PASS' || item.result === 'NA' ? 'bg-green-500' : 'bg-red-500') : 'bg-gray-200'}`}>
                  {item.result === 'PASS' || item.result === 'NA' ? (
                    <CheckCircle className="w-3 h-3 text-white" />
                  ) : item.result === 'FAIL' ? (
                    <span className="text-white text-xs font-bold">✕</span>
                  ) : null}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-2">
                    <p className="text-sm text-gray-800 flex-1">{item.item}</p>
                    {item.critical && <span className="text-xs text-red-500 font-bold flex-shrink-0">★ Critical</span>}
                  </div>
                  {item.result && (
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${rm.bg} ${rm.text}`}>{rm.label}</span>
                      {item.inspector && <span className="text-xs text-gray-400">{item.inspector} · {item.date ? formatDate(item.date) : ''}</span>}
                    </div>
                  )}
                  {item.notes && <p className="text-xs text-gray-500 italic mt-0.5">{item.notes}</p>}
                </div>
                {canRecord && (
                  <button onClick={() => setRecording(item)}
                    className="text-xs btn-secondary py-1 flex-shrink-0">
                    {item.result ? 'Update' : 'Record'}
                  </button>
                )}
              </div>
            )
          })}
        </div>
      )}

      {recording && (
        <RecordModal item={recording} onClose={() => setRecording(null)}
          onRecord={(result, notes) => {
            onRecord(phaseIndex, recording.id, result, notes, user?.fullName ?? 'Inspector')
            setRecording(null)
          }} />
      )}
    </div>
  )
}

// Main page


// ─────────────────────────────────────────────────────────────────
// PHASE 8 — ITS COMMISSIONING REPORT
// Full SCATS/ITS commissioning checklist sign-off report for TMR
// client acceptance and project handover records.
// ─────────────────────────────────────────────────────────────────
function generateCommissioningReport(record: any) {
  const now = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
  const fmtDT = (d: string | null) => d ? new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'

  const totalItems = record.phases.reduce((s: number, p: any) => s + p.items.length, 0)
  const passItems = record.phases.reduce((s: number, p: any) => s + p.items.filter((i: any) => i.result === 'PASS').length, 0)
  const criticalItems = record.phases.reduce((s: number, p: any) => s + p.items.filter((i: any) => i.critical).length, 0)

  const phaseBlocks = record.phases.map((phase: any) => {
    const rows = phase.items.map((item: any, i: number) => `
      <tr style="background:${i%2===0?'#fff':'#f9fafb'}">
        <td style="padding:5px 10px;font-size:9.5pt">${item.critical ? '<strong>⚠ </strong>' : ''}${item.item}</td>
        <td style="padding:5px 8px;text-align:center;font-weight:bold;color:${item.result==='PASS'?'#166534':item.result==='FAIL'?'#b91c1c':'#9ca3af'}">${item.result ?? 'Pending'}</td>
        <td style="padding:5px 8px;font-size:9pt">${item.inspector ?? '—'}</td>
        <td style="padding:5px 8px;text-align:center;font-size:9pt">${item.date ? fmtDT(item.date) : '—'}</td>
        <td style="padding:5px 10px;font-size:9pt;color:#666;font-style:italic">${item.notes ?? ''}</td>
      </tr>`).join('')
    return `<h2>${phase.label}</h2>
    <table class="items"><thead><tr><th style="width:38%">Checklist item</th><th style="width:10%;text-align:center">Result</th><th style="width:16%">Inspector</th><th style="width:12%;text-align:center">Date</th><th style="width:24%">Notes</th></tr></thead>
    <tbody>${rows}</tbody></table>`
  }).join('')

  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>ITS Commissioning Report — ${record.jobNumber}</title>
<style>
*{box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:10pt;color:#111;margin:14mm}
h1{font-size:20pt;font-weight:900;color:#1B3A6B;margin:0}
h2{font-size:10.5pt;color:#1B3A6B;border-bottom:1.5px solid #1B3A6B;padding-bottom:3px;margin:16px 0 8px;page-break-after:avoid}
.hdr{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:3px solid #1B3A6B;padding-bottom:10px;margin-bottom:12px}
.info table{width:100%;border-collapse:collapse;margin-bottom:10px}
.info td{padding:4px 8px;border-bottom:1px solid #f3f4f6}.info td:first-child{width:30%;font-weight:bold;color:#555}
.kpi{display:flex;gap:10px;margin-bottom:12px}
.kpi-box{flex:1;border:1px solid #e5e7eb;border-radius:5px;padding:8px;text-align:center}
.kpi-v{font-size:18pt;font-weight:900}
table.items{width:100%;border-collapse:collapse;font-size:9.5pt;margin-bottom:14px}
th{background:#1B3A6B;color:#fff;padding:5px 8px;text-align:left}
.accept-banner{border-radius:6px;padding:12px 18px;margin:14px 0;text-align:center;font-weight:bold}
.accept-pass{background:#dcfce7;border:2px solid #86efac;color:#166534}
.accept-fail{background:#fee2e2;border:2px solid #fca5a5;color:#b91c1c}
.sig-row{display:flex;gap:30px;margin-top:16px}
.sig-block{flex:1}.sig-line{border-bottom:1.5px solid #333;height:28px;margin-bottom:4px}.sig-lbl{font-size:8pt;color:#888}
.footer{font-size:7.5pt;color:#aaa;text-align:center;margin-top:16px;border-top:1px solid #eee;padding-top:6px}
@media print{body{margin:10mm}}
</style></head><body>
<div class="hdr">
  <div><h1>MCB Civil Electrical</h1><div style="font-size:9pt;color:#888">ABN 44 128 993 087</div></div>
  <div style="text-align:right"><div style="font-size:16pt;font-weight:900;color:#1B3A6B">ITS COMMISSIONING REPORT</div><div style="font-size:9pt;color:#888">Generated: ${now}</div></div>
</div>

<div class="info"><table>
  <tr><td>Project</td><td><strong>${record.title}</strong></td></tr>
  <tr><td>Job reference</td><td>${record.jobNumber}</td></tr>
  <tr><td>Controller type</td><td>${record.controllerType}</td></tr>
  <tr><td>SCATS node ID</td><td style="font-family:monospace">${record.scatsNodeId}</td></tr>
  <tr><td>Client representative</td><td>${record.clientRep}</td></tr>
  <tr><td>Target date</td><td>${record.targetDate ? fmtDT(record.targetDate) : 'TBC'}</td></tr>
</table></div>

<div class="kpi">
  <div class="kpi-box"><div class="kpi-v" style="color:#1B3A6B">${totalItems}</div><div style="font-size:9pt;color:#6b7280">Total items</div></div>
  <div class="kpi-box"><div class="kpi-v" style="color:#166534">${passItems}</div><div style="font-size:9pt;color:#6b7280">Passed</div></div>
  <div class="kpi-box"><div class="kpi-v" style="color:#92400e">${criticalItems}</div><div style="font-size:9pt;color:#6b7280">Critical items</div></div>
  <div class="kpi-box"><div class="kpi-v" style="color:${passItems===totalItems?'#166534':'#d97706'}">${Math.round(passItems/totalItems*100)}%</div><div style="font-size:9pt;color:#6b7280">Complete</div></div>
</div>

<div class="accept-banner ${passItems===totalItems ? 'accept-pass' : 'accept-fail'}">
  ${passItems===totalItems ? '✅ ALL ITEMS PASSED — READY FOR CLIENT SIGN-OFF AND COMMISSIONING ACCEPTANCE' : `⚠ ${totalItems - passItems} ITEM(S) NOT YET PASSED — CANNOT PROCEED TO SIGN-OFF`}
</div>

${phaseBlocks}

<h2>Client acceptance</h2>
<p style="font-size:9.5pt;color:#555">This commissioning report confirms that the SCATS/ITS commissioning checklist for ${record.title} has been completed in accordance with TMR commissioning requirements. By signing below, the client representative confirms acceptance of the commissioned installation.</p>
<div class="sig-row">
  <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">MCB Civil Electrical — commissioning engineer · name &amp; date</div></div>
  <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">${record.clientRep} — client acceptance · date</div></div>
</div>
<div class="footer">MCB Civil Electrical · ITS Commissioning Report · ${record.jobNumber} · ${record.scatsNodeId} · Generated ${now}</div>
</body></html>`
  const w = window.open('', '_blank', 'width=1020,height=820')
  if (!w) { alert('Pop-up blocked'); return }
  w.document.write(html); w.document.close()
  setTimeout(() => { w.focus(); w.print() }, 350)
}
export function ITSCommissioning() {
  const { isTier } = useAuthStore()
  const [records, setRecords] = useState(MOCK_ITS_COMMISSIONING)
  const [selectedId, setSelectedId] = useState<string | null>(records[0]?.id ?? null)
  const selected = records.find(r => r.id === selectedId)

  const canRecord = isTier('D_FIELD')

  const recordResult = (phaseIndex: number, itemId: string, result: string, notes: string, inspector: string) => {
    setRecords(prev => prev.map(rec => {
      if (rec.id !== selectedId) return rec
      return {
        ...rec,
        phases: rec.phases.map((ph, pi) => {
          if (pi !== phaseIndex) return ph
          return {
            ...ph,
            items: ph.items.map(i => i.id !== itemId ? i : {
              ...i, result: result as any, notes: notes || i.notes,
              inspector, date: new Date().toISOString().slice(0,10)
            })
          }
        })
      }
    }))
  }

  const totalItems = selected?.phases.reduce((s, p) => s + p.items.length, 0) ?? 0
  const doneItems  = selected?.phases.reduce((s, p) => s + p.items.filter(i => i.result !== null).length, 0) ?? 0
  const failItems  = selected?.phases.reduce((s, p) => s + p.items.filter(i => i.result === 'FAIL').length, 0) ?? 0
  const pct        = totalItems > 0 ? Math.round(doneItems / totalItems * 100) : 0

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">ITS Commissioning</h1>
        <p className="text-sm text-gray-500 mt-0.5">SCATS signal commissioning checklists</p>
      </div>

      {/* Job selector */}
      <div className="flex flex-wrap gap-2">
        {records.map(r => (
          <button key={r.id} onClick={() => setSelectedId(r.id)}
            className={`text-sm px-4 py-2 rounded-lg border transition-colors ${selectedId === r.id ? 'bg-brand-navy text-white border-brand-navy' : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'}`}>
            {r.jobNumber} — {r.targetDate ? formatDate(r.targetDate) : 'TBC'}
          </button>
        ))}
      </div>

      {selected && (
        <>
          {/* Gap 9 — Live commissioning task completions from Mobile records */}
          {(() => {
            const commissionTasks = MOCK_TASKS.filter(t => t.discipline === 'Commissioning')
            const commissionConfigured = MOCK_CONFIGURED_TASKS.filter(ct =>
              commissionTasks.some(t => ct.drawingTaskId === t.id)
            )
            const allRecords = MOCK_MOBILE_TASK_RECORDS.filter(r =>
              commissionConfigured.some(ct => ct.id === r.taskId) ||
              r.taskTitle.toLowerCase().includes('commiss')
            )
            if (commissionTasks.length === 0) return null
            const accepted = allRecords.filter(r => r.status === 'ACCEPTED').length
            const pending  = allRecords.filter(r => r.status === 'COMPLETE_PENDING_REVIEW').length
            return (
              <div className="bg-blue-50 border border-blue-200 rounded-xl overflow-hidden">
                <div className="px-5 py-3 border-b flex items-center gap-2">
                  <Zap className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-semibold text-blue-900">Live commissioning task completions</span>
                  <span className="text-xs text-blue-600 ml-auto">
                    {accepted} accepted · {pending} pending review
                  </span>
                </div>
                <div className="divide-y divide-blue-100">
                  {commissionTasks.map(task => {
                    const ct = commissionConfigured.find(c => c.drawingTaskId === task.id)
                    const record = ct ? allRecords.find(r => r.taskId === ct.id) : null
                    const m = record ? MOBILE_TASK_META[record.status] : null
                    return (
                      <div key={task.id} className="px-5 py-3 flex items-center gap-3">
                        <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                          record?.status === 'ACCEPTED' ? 'bg-green-500'
                          : record?.status === 'COMPLETE_PENDING_REVIEW' ? 'bg-blue-500'
                          : record?.status === 'IN_PROGRESS' ? 'bg-amber-500'
                          : 'bg-gray-300'
                        }`} />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-gray-900">{task.title}</div>
                          <div className="text-xs text-gray-500">
                            {task.taskNumber} · {task.discipline}
                            {task.holdPointRef && ` · HP: ${task.holdPointRef}`}
                          </div>
                        </div>
                        {m ? (
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${m.bg} ${m.text}`}>{m.label}</span>
                        ) : ct ? (
                          <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Not started</span>
                        ) : (
                          <span className="text-xs bg-amber-100 text-amber-600 px-2 py-0.5 rounded-full">Not pushed to mobile</span>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })()}

          {/* Record header */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="font-bold text-gray-900">{selected.title}</h2>
                  <p className="text-sm text-gray-600 mt-0.5">{selected.controllerType}</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    SCATS Node: {selected.scatsNodeId} · Client rep: {selected.clientRep}
                    {selected.targetDate && ` · Target: ${formatDate(selected.targetDate)}`}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className={`text-2xl font-bold ${failItems > 0 ? 'text-red-600' : pct === 100 ? 'text-green-600' : 'text-blue-600'}`}>{pct}%</div>
                  <div className="text-xs text-gray-400">{doneItems}/{totalItems} items</div>
                  {failItems > 0 && <div className="text-xs text-red-600 font-bold">{failItems} FAIL</div>}
                </div>
              </div>
            </div>
            <div className="h-2 bg-gray-100">
              <div
                className={`h-full transition-all ${failItems > 0 ? 'bg-red-500' : pct === 100 ? 'bg-green-500' : 'bg-blue-500'}`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>

          {failItems > 0 && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-800">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <strong>{failItems} checklist item{failItems !== 1 ? 's' : ''} failed.</strong> These must be resolved and re-tested before TMR sign-off can be obtained.
            </div>
          )}

          {/* Phase checklists */}
          <div className="space-y-3">
            {selected.phases.map((phase, pi) => (
              <PhaseSection key={phase.phase} phase={phase} phaseIndex={pi}
                onRecord={recordResult} canRecord={canRecord} />
            ))}
          </div>

          {/* Export / sign-off */}
          {pct === 100 && failItems === 0 && (
            <div className="bg-green-50 border border-green-200 rounded-xl px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="font-semibold text-green-800">All items complete — ready for client sign-off</span>
              </div>
              <button onClick={() => generateCommissioningReport(selected)} className="btn-primary bg-green-600 hover:bg-green-700 text-sm">Generate commissioning report</button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
